import * as React from 'react';
import { AutoAwesome } from '@mui/icons-material'
import { Button, type SxProps, type Theme, Typography } from '@mui/material'
import { Popover } from '@workwave-tidal/core/components'
import { useWormhole } from '@workwave-tidal/core/hooks'
import { Children, useRef, useState } from 'react'
import { useConversationTexts } from '../LocaleProvider.js'
import { useWormholeId } from '../WormholeIdProvider.js'
import {
  ConversationSuggestionsRoot,
  type ConversationSuggestionsRootProps,
} from './components/ConversationSuggestionsRoot.js'
import { useInsertSuggestion } from './useInsertSuggestion.js'

export type ConversationSuggestionsControlsProps = {
  /**
   * data-testid used for the root Stack component
   */
  testId?: string

  /**
   * additional class name to pass to the root Stack component
   */
  className?: string

  /**
   * allows to pass additional styles to the root Stack component
   */
  sx?: SxProps<Theme>

  slotProps?: {
    root?: Omit<ConversationSuggestionsRootProps, 'children' | 'sx' | 'className'>
  }

  hidden?: boolean
  disabled?: boolean
}

export function ConversationSuggestionsControls(props: ConversationSuggestionsControlsProps) {
  const {
    sx,
    testId = 'TidalConversationSuggestions',
    hidden,
    className,
    disabled,
    slotProps: { root } = {},
  } = props

  useInsertSuggestion()

  const { worms: suggestions } = useWormhole(useWormholeId('editorSuggestions'))

  const [showSuggestions, setShowSuggestions] = useState(false)
  const suggestionRef = useRef<HTMLDivElement | null>(null)

  const {
    suggestionsControl: { trigger, suggestionsPanel },
  } = useConversationTexts()

  if (hidden) return null
  if (Children.count(suggestions) === 0) return null

  return (
    <>
      <div ref={suggestionRef}>
        <Button startIcon={<AutoAwesome />} onClick={() => setShowSuggestions(p => !p)} disabled={disabled}>
          {trigger.label}
        </Button>
      </div>

      <Popover
        arrow={false}
        open={showSuggestions}
        placement="top-start"
        anchorEl={suggestionRef.current || undefined}
        onClick={() => setShowSuggestions(false)}
        onClickAway={() => setShowSuggestions(false)}
      >
        {/* TODO: Make the min-max size of the popover aware of the parent web-chat size */}
        <ConversationSuggestionsRoot
          useFlexGap
          {...root}
          sx={sx}
          className={className}
          data-testid={testId}
          data-tidal="ConversationSuggestions"
        >
          <Typography variant="h6" sx={{ width: '100%' }} gutterBottom>
            {suggestionsPanel.title}
          </Typography>
          {suggestions}
        </ConversationSuggestionsRoot>
      </Popover>
    </>
  )
}
